import { GlassWater, Check, AlarmClock, BellOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LangContext";
import { LANG_TAG } from "@/lib/geo";
import { readHydration, drinksToday, EVERY_MIN } from "@/lib/hydration";
import { fill } from "@/lib/utils";

// Modal shown app-wide when a 30-minute drink slot is due during an active hydration session.
export function DrinkAlertDialog({ alert, onDrank, onSnooze, onStop, onClose }) {
  const { tc, lang } = useLang();
  if (!alert) return null;
  const s = readHydration();
  const glasses = drinksToday(s);
  const until = s.until ? new Date(s.until).toLocaleTimeString(LANG_TAG[lang] || "en-US", { hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-sm overflow-hidden border-sky-500/40 p-0" data-testid="drink-alert-dialog" data-slot={alert.slot}>
        <div className="bg-sky-600 px-6 pb-6 pt-8 text-center text-white">
          <span className="relative mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/30">
            <GlassWater className="h-7 w-7" />
            <span className="absolute inset-0 rounded-full ring-2 ring-white/40 animate-ping" />
          </span>
          <DialogHeader className="mt-4 space-y-1 text-center sm:text-center">
            <DialogDescription className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-100">
              {tc({ id: "Pengingat hidrasi", en: "Hydration reminder", ar: "تذكير الترطيب" })}
            </DialogDescription>
            <DialogTitle className="font-serif text-3xl text-white" data-testid="drink-alert-title">
              {tc({ id: "Waktunya minum air", en: "Time to drink water", ar: "وقت شرب الماء" })}
            </DialogTitle>
          </DialogHeader>
          <p className="mt-3 text-sm text-white/90 leading-relaxed">
            {tc({ id: "Minum 200–250 ml air sekarang, sedikit demi sedikit. Tambahkan kurma atau oralit untuk elektrolit.", en: "Drink 200–250 ml of water now, in small sips. Add dates or an electrolyte drink.", ar: "اشرب 200–250 مل من الماء الآن على رشفات، وأضف التمر أو محلول الإلكتروليت." })}
          </p>
        </div>

        <div className="space-y-3 px-6 py-5">
          <p className="text-center text-xs text-muted-foreground tabular-nums" data-testid="drink-alert-status">
            {until
              ? fill(tc({ id: "{n} gelas hari ini · pengingat tiap {min} mnt sampai {time}", en: "{n} glasses today · reminders every {min} min until {time}", ar: "{n} أكواب اليوم · تذكير كل {min} د حتى {time}" }), { n: glasses, min: EVERY_MIN, time: until })
              : fill(tc({ id: "{n} gelas hari ini", en: "{n} glasses today", ar: "{n} أكواب اليوم" }), { n: glasses })}
          </p>
          <Button className="w-full rounded-full bg-sky-600 hover:bg-sky-700" onClick={onDrank} data-testid="drink-alert-drank">
            <Check className="h-4 w-4 me-1.5" />{tc({ id: "Sudah minum", en: "I drank", ar: "شربت" })}
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="rounded-full" onClick={onSnooze} data-testid="drink-alert-snooze">
              <AlarmClock className="h-4 w-4 me-1.5" />{tc({ id: "Tunda 10 mnt", en: "Snooze 10 min", ar: "تأجيل 10 د" })}
            </Button>
            <Button variant="ghost" className="rounded-full text-muted-foreground" onClick={onStop} data-testid="drink-alert-stop">
              <BellOff className="h-3.5 w-3.5 me-1.5" />{tc({ id: "Hentikan pengingat", en: "Stop reminders", ar: "إيقاف التذكير" })}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
